import h from 'hyperscript';

import { EMBED_TYPES } from 'src/constants';
import { denormalize } from 'utilities/math';
import { safeStringify } from 'utilities/parsers';
import { DEFAULT_ASPECT_RATIO } from 'configuration/defaults';
import { getCurrentScriptSource } from 'utilities/dom';
import {
    camelToDashCase,
    toQueryString,
    objToAttributes
} from 'utilities/formatting';

export const baseIFrameAttrs = {
    allow: 'autoplay; fullscreen',
    scrolling: 'no',
    className: 'hapyak-embed',
    allowtransparency: 'true',
    allowfullscreen: 'allowfullscreen',
    webkitallowfullscreen: 'webkitallowfullscreen',
    mozallowfullscreen: 'mozallowfullscreen',
    style: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '0',
        border: 'none',
        ['min-width']: '100%',
        ['min-height']: '100%'
    }
};

export const toIFrameOptions = ({
    src,
    lang,
    language,
    thumbnail,
    variables,
    loop,
    autoplay,
    captions,
    playButton,
    start,
    seo,
    refId,
    uid,
    ...options
}) => {
    const compiled = {},
        overrides = {
            project: {},
            state: {},
            variables: {}
        };

    if (defined(thumbnail)) {
        overrides.project.thumbnail = thumbnail;
    }

    if (defined(playButton)) {
        overrides.project.playButton = playButton;
    }

    if (defined(lang) || defined(language)) {
        overrides.state.language = lang || language;
    }

    if (defined(variables)) {
        overrides.variables = variables;
    }

    if (defined(seo)) {
        overrides.project.seo = {
            enabled: seo
        }
    }

    if (defined(refId)) {
        overrides.analytics = {
            ...(overrides.analytics || {}),
            refId
        }
    }

    if (defined(uid)) {
        overrides.analytics = {
            ...(overrides.analytics || {}),
            uid
        }
    }

    compiled.overrides = safeStringify(overrides);

    if (defined(autoplay)) {
        compiled.autoplay = autoplay;
    }

    if (defined(captions)) {
        compiled.captions = captions;
    }

    if (defined(loop)) {
        compiled.loop = loop;
    }

    if (defined(start)) {
        compiled.startTime = start;
    }

    return compiled;
};

export const generateIFrameEmbed = ({
    src,
    baseUrl = __webpack_public_path__,
    options = {}
}) => {
    const optionsQueryString = toQueryString(options);

    const attrs = {
        ...baseIFrameAttrs,
        src: `${baseUrl}iframe.html?overridesUrl=${src}${optionsQueryString}`
    };

    return <iframe { ...attrs } />;
};

export const generateCustomTagEmbed = ({ 
    src,
    width = 'auto',
    height = 'auto',
    presentation = 'lightbox',
    autoplay = 'false',
    loop = 'false',
    variables = {},
    ...options
}) => (
    <hapyak-embed 
        src={ src }
        width={ width }
        height={ height }
        autoplay={ autoplay }
        presentation={ presentation }
        loop={ loop }
        { ...options }
    >
    {
        options.title ? <title>{ options.title }</title> : null
    }
    {
        options.description ? <description>{ options.description }</description> : null
    }
    {
        Object.keys(variables).map(key => <variable name={ key }>{ variables[key] }</variable>)
    }
    </hapyak-embed>
);

export const generateCustomTagString = (
    { ...options },
    baseUrl = __webpack_public_path__
) => {
    const childOpts = ['title', 'description', 'variables'],
        noValOpts = ['loop', 'autoplay', 'playButton', 'captions', 'seo'];

    const attrs = (
        Object
            .keys(options)
            .filter(key => !childOpts.includes(key))
            .map(key => {
                if (noValOpts.includes(key) && options[key] === true) {
                    return camelToDashCase(key);
                } 

                if (key === 'language') {
                    return `lang="${options[key]}"`;
                }

                if (key === 'textLocation') {
                    return `text-location="${options[key]}"`;
                }

                if (key === 'refId') {
                    return `ref-id="${options[key]}"`;
                }

                return `${key}="${options[key]}"`;
            })
            .join(' ')
    ),
    children = (
        childOpts
            .filter(opt => options[opt])
            .reduce((c, opt) => {
                if (opt === 'title' || opt === 'description') {
                    c += `\n    <${opt}>${options[opt]}</${opt}>`
                } else if (opt === 'variables') {
                    c += '\n';
                    c += Object.keys(options[opt]).map(key => `    <variable name="${key}">${options[opt][key]}</variable>`).join("\n")
                    c += '\n';
                }

                return c;
            }, '')
    );

    return (
`
<script src="${baseUrl}embed.js"></script>
<hapyak-embed ${attrs}>${children}</hapyak-embed>
`.trim()
);
};

export const generateIFrameEmbedString = (
    {
        src,
        ...options
    },
    baseUrl = __webpack_public_path__,
    aspectRatio = `${denormalize(DEFAULT_ASPECT_RATIO)}%`,
) => {
    const params = toQueryString(toIFrameOptions(options)),
        attrs = objToAttributes(baseIFrameAttrs),
        containerStyles = `padding-top: ${aspectRatio}`;

    return (
`
<script src="${baseUrl}helper.js"></script>
<div style="position: relative; display: block; max-width: 100%;">
    <div class="player-container hapyak-player-embed" style="${containerStyles}">
        <iframe src="${baseUrl}iframe.html?overridesUrl=${src}${params}" ${attrs}></iframe>
    </div>
</div>
`.trim()
    )
};

export const generateEmbed = (config) => {
    const type = config.type || EMBED_TYPES.CUSTOM_ELEMENT;

    switch(type) {
        case EMBED_TYPES.CUSTOM_ELEMENT:
            return generateCustomTagEmbed(config);

        case EMBED_TYPES.IFRAME:
            return generateIFrameEmbed(config);
    }
};

// using this until `is` stops importing all the Annotations for the `expandable` check 
function defined(subject) {
    return typeof subject !== 'undefined' && subject !== null;
}